import { readFile } from 'fs/promises';
import { resolve, dirname } from 'path';

const IMPORT_REGEX = /^@(.+)$/gm;
const CODE_BLOCK_REGEX = /```[\s\S]*?```/g;
const INLINE_CODE_REGEX = /`[^`]+`/g;
const MAX_DEPTH = 5;

/**
 * Options for import resolution
 */
export interface ImportResolverOptions {
  strictMode?: boolean;
  maxDepth?: number;
}

/**
 * Result of import resolution
 */
export interface ResolveResult {
  content: string;
  warnings: string[];
  errors: string[];
}

/**
 * Resolve @import statements in markdown content
 */
export class ImportResolver {
  private options: Required<ImportResolverOptions>;
  private visited: Set<string>;
  private warnings: string[];
  private errors: string[];

  constructor(options: ImportResolverOptions = {}) {
    this.options = {
      strictMode: options.strictMode ?? false,
      maxDepth: options.maxDepth ?? MAX_DEPTH,
    };
    this.visited = new Set();
    this.warnings = [];
    this.errors = [];
  }

  /**
   * Resolve all imports in a file
   * @param filePath - Path to the markdown file
   * @returns Resolved content with imports expanded
   */
  async resolveFile(filePath: string): Promise<ResolveResult> {
    this.visited.clear();
    this.warnings = [];
    this.errors = [];

    try {
      const content = await readFile(filePath, 'utf-8');
      const resolved = await this.resolveContent(content, dirname(filePath), 0);
      return {
        content: resolved,
        warnings: this.warnings,
        errors: this.errors,
      };
    } catch (error) {
      this.errors.push(`Failed to read file ${filePath}: ${(error as Error).message}`);
      return {
        content: '',
        warnings: this.warnings,
        errors: this.errors,
      };
    }
  }

  /**
   * Resolve imports in content string
   * @param content - Markdown content
   * @param basePath - Base directory for resolving relative imports
   * @param depth - Current recursion depth
   * @returns Content with imports resolved
   */
  private async resolveContent(content: string, basePath: string, depth: number): Promise<string> {
    // Check depth limit
    if (depth >= this.options.maxDepth) {
      this.warnings.push(`Maximum import depth (${this.options.maxDepth}) reached`);
      return content;
    }

    // Remove code blocks to avoid processing imports within them
    const codeBlocks: string[] = [];
    let processed = content.replace(CODE_BLOCK_REGEX, (match) => {
      const placeholder = `__CODE_BLOCK_${codeBlocks.length}__`;
      codeBlocks.push(match);
      return placeholder;
    });

    // Remove inline code
    const inlineCodes: string[] = [];
    processed = processed.replace(INLINE_CODE_REGEX, (match) => {
      const placeholder = `__INLINE_CODE_${inlineCodes.length}__`;
      inlineCodes.push(match);
      return placeholder;
    });

    // Process imports line by line
    const lines = processed.split('\n');
    const resolvedLines: string[] = [];

    for (const line of lines) {
      const match = line.match(/^@(.+)$/);
      if (match) {
        const importPath = match[1].trim();
        const resolved = await this.resolveImport(importPath, basePath, depth);
        resolvedLines.push(resolved);
      } else {
        resolvedLines.push(line);
      }
    }

    let result = resolvedLines.join('\n');

    // Restore inline code
    inlineCodes.forEach((code, index) => {
      result = result.replace(`__INLINE_CODE_${index}__`, code);
    });

    // Restore code blocks
    codeBlocks.forEach((block, index) => {
      result = result.replace(`__CODE_BLOCK_${index}__`, block);
    });

    return result;
  }

  /**
   * Resolve a single import statement
   * @param importPath - Path from @import statement
   * @param basePath - Base directory
   * @param depth - Current depth
   * @returns Resolved content or original import statement
   */
  private async resolveImport(
    importPath: string,
    basePath: string,
    depth: number
  ): Promise<string> {
    // Resolve absolute or relative path
    const absolutePath = importPath.startsWith('/')
      ? importPath
      : resolve(basePath, importPath);

    // Check for circular imports
    if (this.visited.has(absolutePath)) {
      const warning = `Circular import detected: ${absolutePath}`;
      this.warnings.push(warning);
      return `<!-- ${warning} -->`;
    }

    this.visited.add(absolutePath);

    try {
      const importedContent = await readFile(absolutePath, 'utf-8');

      // Recursively resolve imports in the imported content
      const resolved = await this.resolveContent(importedContent, dirname(absolutePath), depth + 1);

      return resolved;
    } catch (error) {
      const errorMsg = `Failed to resolve import: ${importPath} (${absolutePath})`;

      if (this.options.strictMode) {
        this.errors.push(errorMsg);
        throw new Error(errorMsg);
      } else {
        this.warnings.push(errorMsg);
        // Return original import statement if not in strict mode
        return `@${importPath}`;
      }
    }
  }

  /**
   * Check if content contains any @import statements
   * @param content - Markdown content
   * @returns true if content has imports
   */
  static hasImports(content: string): boolean {
    // Remove code blocks and inline code first
    const withoutCode = content
      .replace(CODE_BLOCK_REGEX, '')
      .replace(INLINE_CODE_REGEX, '');

    return IMPORT_REGEX.test(withoutCode);
  }

  /**
   * Extract all import paths from content
   * @param content - Markdown content
   * @returns Array of import paths
   */
  static extractImports(content: string): string[] {
    const imports: string[] = [];

    // Remove code blocks and inline code
    const withoutCode = content
      .replace(CODE_BLOCK_REGEX, '')
      .replace(INLINE_CODE_REGEX, '');

    const lines = withoutCode.split('\n');
    for (const line of lines) {
      const match = line.match(/^@(.+)$/);
      if (match) {
        imports.push(match[1].trim());
      }
    }

    return imports;
  }
}
